/**
 * Announcements (/announcements, admin only).
 *
 * Staff write short notes that clients must read and dismiss on the
 * proposal loading screen. Only one announcement can be switched on
 * at a time; the backend refuses a second with 409 and the clash is
 * surfaced here.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, Megaphone, Plus } from "lucide-react";
import StaffShell from "../components/staff/StaffShell";
import { Button } from "../components/ui/button";
import { AnnouncementRow } from "../components/announcements/AnnouncementRow";
import { AnnouncementForm } from "../components/announcements/AnnouncementForm";
import { AnnouncementPreview } from "../components/announcements/AnnouncementPreview";
import { announcementError } from "../components/announcements/errors";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Announcements() {
  const [items, setItems] = useState([]);
  const [enabledCount, setEnabledCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (new) | item
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/announcements`);
      setItems(data.announcements || []);
      setEnabledCount(data.enabled_count || 0);
    } catch (e) {
      toast.error(announcementError(e, "Could not load announcements"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (item, enabled) => {
    try {
      const { data } = await axios.post(`${API}/api/announcements/${item.id}/toggle`, { enabled });
      setItems((list) => list.map((x) => (x.id === item.id ? data : x)));
      setEnabledCount((n) => n + (enabled ? 1 : -1));
      toast.success(enabled ? "Announcement switched on" : "Announcement switched off");
    } catch (e) {
      toast.error(announcementError(e, "Could not change this announcement"), { duration: 8000 });
    }
  };

  const remove = async (item) => {
    try {
      await axios.delete(`${API}/api/announcements/${item.id}`);
      setItems((list) => list.filter((x) => x.id !== item.id));
      if (item.enabled) setEnabledCount((n) => n - 1);
      toast.success("Announcement deleted");
    } catch (e) {
      toast.error(announcementError(e, "Could not delete this announcement"));
    }
  };

  return (
    <StaffShell>
      <div className="max-w-4xl space-y-8" data-testid="announcements-page">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">Announcements</h1>
            <p className="text-sm text-stone-500 mt-1 max-w-2xl">
              A note clients read on the loading screen when they open a proposal link. They must press
              Continue before the proposal appears. Only one announcement can be switched on at a time.
            </p>
          </div>
          <Button
            onClick={() => setEditing({})}
            data-testid="announcement-add-btn"
            className="bg-[#1B4F8A] hover:bg-[#173f6e]"
          >
            <Plus className="w-4 h-4 mr-2" />
            New announcement
          </Button>
        </div>

        {enabledCount > 1 && (
          <div
            className="flex items-start gap-3 rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-800"
            data-testid="announcement-clash-alert"
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              {enabledCount} announcements are switched on. Clients only ever see one, so please switch the
              others off.
            </span>
          </div>
        )}

        <div className="border border-stone-200 rounded-xl overflow-hidden bg-white divide-y divide-stone-100" data-testid="announcement-list">
          {loading ? (
            <p className="px-5 py-8 text-sm text-stone-500" data-testid="announcement-loading">Loading announcements…</p>
          ) : items.length === 0 ? (
            <div className="px-5 py-12 text-center" data-testid="announcement-empty">
              <Megaphone className="w-8 h-8 mx-auto text-stone-300" />
              <p className="mt-3 text-sm text-stone-600">No announcements yet.</p>
              <p className="text-xs text-stone-400 mt-1">Write one and switch it on when you want clients to see it.</p>
            </div>
          ) : (
            items.map((item) => (
              <AnnouncementRow
                key={item.id}
                item={item}
                onToggle={(enabled) => toggle(item, enabled)}
                onEdit={() => setEditing(item)}
                onPreview={() => setPreview(item)}
                onDelete={() => remove(item)}
              />
            ))
          )}
        </div>
      </div>

      {editing !== null && (
        <AnnouncementForm
          item={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
      {preview && <AnnouncementPreview item={preview} onClose={() => setPreview(null)} />}
    </StaffShell>
  );
}
